import { useRef, useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LabelGroup, Label } from '../../shared/types';
import { GPanelContactsPermissions } from '../../../../../utils/roles/client-roles';
import { useGpanelContactsPermissions } from '../../shared/hooks/use-gpanel-contacts-permissions';
import { Messages } from '../../../../../utils/message-constants';
import GpanelContactsAddLabelDialog from './dialogs/gpanel-contacts-add-label-dialog';
import GpanelContactsEditLabelDialog from './dialogs/gpanel-contacts-edit-label-dialog';
import GpanelContactsDeleteLabelDialog from './dialogs/gpanel-contacts-delete-label-dialog';
import { GPanelContactsPagesConfig } from '../../shared/pages-config';
import {
  getSyncsByLabelId,
  GPanelContactsKeys,
} from '../../gpanel-contacts-api';
import { useOrganizationDirectoryContactsQuery } from '../../gpanel-contacts-queries';

import Stack from '@mui/material/Stack';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import LabelIcon from '@mui/icons-material/Label';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Sync from '@mui/icons-material/Sync';

interface GpanelContactsLabelListProps {
  labelGroups?: LabelGroup[];
  selectedLabelId: Label['id'];
  onLabelClick: (label: Label) => void;
  isLoading: boolean;
}

export default function GpanelContactsLabelList({
  labelGroups,
  selectedLabelId,
  onLabelClick: onLabelClick,
  isLoading,
}: GpanelContactsLabelListProps) {
  const { PERMISSIONS } = useGpanelContactsPermissions();
  const [isGroupOpen, setIsGroupOpen] = useState(true);

  const [labelForAddLabelDialog, setLabelForAddLabelDialog] =
    useState<Label | null>(null);
  const [labelForEditLabelDialog, setLabelForEditLabelDialog] =
    useState<Label | null>(null);
  const [labelForDeleteLabelDialog, setLabelForDeleteLabelDialog] =
    useState<Label | null>(null);

  const { data: contacts } = useOrganizationDirectoryContactsQuery();

  const contactCountsByLabelId = useMemo(() => {
    const counts = new Map<number, number>();
    if (!contacts) {
      return counts;
    }
    for (const contact of contacts) {
      if (contact.labelIds) {
        for (const labelId of contact.labelIds) {
          counts.set(labelId, (counts.get(labelId) || 0) + 1);
        }
      }
    }
    return counts;
  }, [contacts]);

  // Get all label IDs for fetching sync data
  const allLabelIds =
    labelGroups?.flatMap(
      (group) => group.labels?.map((label) => label.id) || []
    ) || [];

  // Fetch sync data for all labels
  const { data: allLabelSyncs = [] } = useQuery({
    queryKey: [GPanelContactsKeys.LabelSyncs, ...allLabelIds],
    queryFn: async () => {
      if (allLabelIds.length === 0) return [];
      const syncPromises = allLabelIds.map((labelId) =>
        getSyncsByLabelId(labelId.toString())
      );
      const syncResults = await Promise.all(syncPromises);
      const flatResults = syncResults.flat();

      return flatResults;
    },
    enabled: allLabelIds.length > 0,
  });

  // Create a lookup for sync status by label ID
  const syncStatusByLabelId: Record<
    number,
    { isActive: boolean; isDisconnected: boolean }
  > = {};
  allLabelSyncs.forEach((sync) => {
    syncStatusByLabelId[sync.labelId] = {
      isActive: sync.status === 'COMPLETED',
      isDisconnected: sync.status === 'DISCONNECTED',
    };
  });

  const GroupConfigs = {
    name: GPanelContactsPagesConfig.OrganizationLabels.name,
    createPermission: {
      value: PERMISSIONS.createGpanelLabels,
      name: GPanelContactsPermissions.CreateGpanelLabels.name,
    },
    editPermission: {
      value: PERMISSIONS.editGpanelLabels,
      name: GPanelContactsPermissions.EditGpanelLabels.name,
    },
    deletePermission: {
      value: PERMISSIONS.trashGpanelLabels,
      name: GPanelContactsPermissions.TrashGpanelLabels.name,
    },
  } as const;

  if (isLoading) {
    return (
      <Stack gap={2.5} alignItems="center">
        {Array.from({ length: 10 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={15} width="70%" />
        ))}
      </Stack>
    );
  }

  return (
    <Stack
      flexDirection="column"
      alignItems="center"
      sx={{
        overflow: 'hidden',
        overflowY: 'auto',
      }}
    >
      {labelGroups?.map((group, index) => {
        return (
          <List
            key={index}
            sx={{
              width: '100%',
              py: 0,
              '& .MuiListItemButton-root': {
                borderRadius: 2,
              },
            }}
          >
            <ListItemButton
              onClick={() => setIsGroupOpen((prev) => !prev)}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: '5px',
                pl: '8px',
                pr: '24px',
              }}
            >
              {isGroupOpen ? <ArrowDropDown /> : <ArrowDropUp />}
              <ListItemText
                primary={GroupConfigs.name}
                slotProps={{
                  primary: {
                    sx: {
                      fontWeight: 400,
                    },
                  },
                }}
              />
              <Tooltip
                arrow
                title={
                  !GroupConfigs.createPermission.value
                    ? Messages.Errors.Permission.Acting(
                        GroupConfigs.createPermission.name
                      )
                    : 'Add Label'
                }
              >
                <Stack
                  sx={{
                    transform: 'translateX(20px)',
                  }}
                >
                  <IconButton
                    aria-label="add"
                    onClick={(event) => {
                      event.stopPropagation();
                      setLabelForAddLabelDialog({} as Label); // Open dialog
                    }}
                    disabled={!GroupConfigs.createPermission.value}
                    size="small"
                  >
                    <Add />
                  </IconButton>
                </Stack>
              </Tooltip>
            </ListItemButton>
            <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {group.labels
                  ?.filter((label) => !label.trashed)
                  .map((label, index) => {
                    // Get sync status for this label
                    const syncStatus = syncStatusByLabelId[label.id];

                    return (
                      <ListItem
                        key={label.id ?? index}
                        sx={[
                          {
                            py: 0,
                            pr: 0,
                          },
                          {
                            '& .count': { display: 'block' },
                            '&:hover .count': { display: 'none' },
                          },
                          {
                            '& .actions': { display: 'none' },
                            '&:hover .actions': { display: 'block' },
                          },
                        ]}
                        secondaryAction={
                          <>
                            <Typography className="count" color="textSecondary">
                              {contactCountsByLabelId.get(label.id) ?? 0}
                            </Typography>
                            <Stack
                              className="actions"
                              flexDirection="row"
                              justifyContent="flex-start"
                              gap={0.25}
                              sx={{ position: 'relative', left: 16 }}
                            >
                              <Tooltip
                                arrow
                                title={
                                  !GroupConfigs.editPermission.value
                                    ? Messages.Errors.Permission.Acting(
                                        GroupConfigs.editPermission.name
                                      )
                                    : 'Edit'
                                }
                              >
                                <Typography component="span">
                                  <IconButton
                                    onClick={() =>
                                      setLabelForEditLabelDialog(label)
                                    }
                                    disabled={
                                      !GroupConfigs.editPermission.value
                                    }
                                    aria-label="edit"
                                    size="small"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Typography>
                              </Tooltip>
                              <Tooltip
                                arrow
                                title={
                                  !GroupConfigs.deletePermission.value
                                    ? Messages.Errors.Permission.Acting(
                                        GroupConfigs.deletePermission.name
                                      )
                                    : syncStatus && syncStatus.isActive
                                    ? 'Unable to delete a label with an associated contact sync. Please delete the contact sync first.'
                                    : 'Trash Label'
                                }
                              >
                                <Typography component="span">
                                  <IconButton
                                    onClick={() =>
                                      setLabelForDeleteLabelDialog(label)
                                    }
                                    disabled={
                                      !GroupConfigs.deletePermission.value ||
                                      (syncStatus && syncStatus.isActive)
                                    }
                                    aria-label="delete"
                                    size="small"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </>
                        }
                      >
                        <ListItemButton
                          onClick={() => onLabelClick(label)}
                          selected={selectedLabelId == label.id}
                          sx={{
                            pl: 1.5,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 28 }}>
                            <LabelIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            slotProps={{
                              primary: {
                                sx: {
                                  width: 150,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                },
                              },
                            }}
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  justifyContent: 'start',
                                }}
                              >
                                <LabelNameWithTooltip name={label.name} />

                                {(label.syncRunning ||
                                  (syncStatus && syncStatus.isActive)) && (
                                  <Tooltip title="Sync is active">
                                    <Sync fontSize="small" color="primary" />
                                  </Tooltip>
                                )}

                                {!label.syncRunning &&
                                  syncStatus &&
                                  syncStatus.isDisconnected && (
                                    <Tooltip title="Sync Disconnected">
                                      <Sync fontSize="small" color="disabled" />
                                    </Tooltip>
                                  )}
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}

                {group.labels?.filter((label) => !label.trashed).length ===
                  0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'start',
                      pl: '40px',
                      py: 1,
                    }}
                  >
                    <Typography
                      color="textSecondary"
                      sx={{ fontStyle: 'italic' }}
                    >
                      No labels
                    </Typography>
                  </Box>
                )}
              </List>
            </Collapse>
          </List>
        );
      })}

      {labelForAddLabelDialog && (
        <GpanelContactsAddLabelDialog
          handleClose={() => setLabelForAddLabelDialog(null)}
        />
      )}
      {labelForEditLabelDialog && (
        <GpanelContactsEditLabelDialog
          handleClose={() => setLabelForEditLabelDialog(null)}
          label={labelForEditLabelDialog}
        />
      )}
      {labelForDeleteLabelDialog && (
        <GpanelContactsDeleteLabelDialog
          handleClose={() => setLabelForDeleteLabelDialog(null)}
          label={labelForDeleteLabelDialog}
        />
      )}
    </Stack>
  );
}

// Helper: Only show tooltip if label name is truncated
function LabelNameWithTooltip({ name }: { name: string }) {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [name]);
  const labelName = (
    <Typography
      ref={textRef}
      component="span"
      sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
        display: 'inline-block',
      }}
    >
      {name}
    </Typography>
  );
  return isTruncated ? (
    <Tooltip title={name} arrow placement="top">
      {labelName}
    </Tooltip>
  ) : (
    labelName
  );
}
